import { useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { T, Icon } from '../ProjectOverview/parts/stitch';
import SourceSearchBox from '../Offertes/SourceSearchBox';
import ProjectCorrespondence from '../ProjectOverview/ProjectCorrespondence';

/**
 * Bronnen aan een project hangen — gedeeld door de projectflow (StagePanel) en de
 * projectstrook op de callsheet (28 sep). Eén plek, zodat "E-mail koppelen" en de
 * klantbriefing-upload overal precies hetzelfde doen.
 */

// Mail/notulen/meeting koppelen — treffer uit de SourceSearchBox landt als projectnotitie.
// Geeft true terug als het gelukt is (de aanroeper ververst zijn eigen lijst).
export async function koppelBronAlsNotitie(projectRecId, blok) {
  const tekst = String(blok || '').trim();
  if (!tekst || !projectRecId) return false;
  const kop = (tekst.split('\n')[0] || '').replace(/^[—\s]+|[—\s]+$/g, '');
  try {
    const r = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectId: projectRecId,
        title: (kop || 'Gekoppelde e-mail').slice(0, 120),
        notes: tekst,
        source: 'Koppeling (mail/notulen)',
      }),
    });
    if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
    toast.success('Gekoppeld als projectnotitie — staat bij Notities');
    return true;
  } catch (e) {
    toast.error(`Koppelen mislukt: ${e.message}`);
    return false;
  }
}

// Het "E-mail koppelen"-vak zelf. Eerst de mails die bij dít project horen (zelfde lijst als
// tabblad Correspondentie: contactpersonen, projectnummer, met 'niet relevant'), daarna vrij
// zoeken voor wat de match mist (Joris 28 sep: vrij zoeken gaf vooral algemene mails).
export function MailKoppelBox({ projectRecId, projectNumber = '', projectName = '', contactEmail = '', onGekoppeld }) {
  return (
    <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '0.75rem', border: `1px solid ${T.surfaceHigh}`, bgcolor: T.surface }}>
      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: T.outline, display: 'block', mb: 0.5 }}>
        Koppel mails, OneNote-pagina's of meetings als projectnotitie (zichtbaar onder Notes). Hieronder eerst de mails die bij dit project horen.
      </Typography>
      <ProjectCorrespondence active projectId={projectRecId} projectNumber={projectNumber}
        projectName={projectName} contactEmail={contactEmail} />
      <Box component="details" sx={{ mt: 1.5 }}>
        <Box component="summary" sx={{ cursor: 'pointer', fontSize: 12.5, color: '#64748b', fontWeight: 600 }}>
          Zelf zoeken — ook andere mails, OneNote-notulen en meetings
        </Box>
        <Box sx={{ mt: 1 }}>
          <SourceSearchBox
            onAddText={async (blok) => { if (await koppelBronAlsNotitie(projectRecId, blok)) await onGekoppeld?.(); }}
            label="Zoek in e-mail, notulen & meetings (klantnaam of onderwerp)" />
        </Box>
      </Box>
    </Box>
  );
}

// Klantbriefing: tekst uit een bestand halen (zelfde parser als overal).
export async function leesBriefingBestand(file) {
  const fd = new FormData();
  fd.append('file', file);
  const pr = await authFetch(`${BACKEND_URL}/Api/parse-attachment`, { method: 'POST', body: fd });
  if (!pr.ok) throw new Error((await pr.json().catch(() => ({}))).error || 'Kon het document niet lezen');
  const { text } = await pr.json();
  if (!String(text || '').trim()) throw new Error('Geen tekst gevonden in het document');
  return text;
}

// Klantbriefing BEWAREN bij het project (de engine analyseert daarna). Geeft de response terug.
export async function bewaarKlantbriefing(projectRecId, { tekst, bestandsnaam, bron }) {
  const r = await authFetch(`${BACKEND_URL}/Api/project-flow/${projectRecId}/briefings`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tekst, bestandsnaam, bron }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`);
  return r.json();
}

// Sleepvak voor de klantbriefing (buiten de projectflow, bv. op de callsheet).
export function KlantbriefingDropzone({ projectRecId, onBewaard }) {
  const [bezig, setBezig] = useState(false);
  const [slepen, setSlepen] = useState(false);
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file || bezig || !projectRecId) return;
    setBezig(true);
    try {
      const tekst = await leesBriefingBestand(file);
      const d = await bewaarKlantbriefing(projectRecId, { tekst, bestandsnaam: file.name, bron: 'upload' });
      // Analyse draait sinds 2.25.415 op de achtergrond; de status staat in de projectflow.
      toast.success('Briefing bewaard · AI-check loopt');
      await onBewaard?.(d);
    } catch (e) { toast.error(`Briefing opslaan mislukt: ${e.message}`); }
    finally { setBezig(false); }
  };
  return (
    <Box
      onDragOver={(e) => { e.preventDefault(); setSlepen(true); }}
      onDragLeave={() => setSlepen(false)}
      onDrop={(e) => { e.preventDefault(); setSlepen(false); upload(e.dataTransfer?.files?.[0]); }}
      onClick={() => !bezig && fileRef.current?.click()}
      sx={{
        p: 2, borderRadius: '0.75rem', cursor: 'pointer', textAlign: 'center',
        border: `2px dashed ${slepen ? T.tertiary : T.outlineVariant}`,
        bgcolor: slepen ? T.tertiaryContainer + '40' : 'transparent',
        transition: 'all 0.15s ease', '&:hover': { borderColor: T.tertiary },
      }}>
      <input ref={fileRef} type="file" hidden accept=".pdf,.docx,.pptx,.txt,.md"
        onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }} />
      <Typography variant="caption" sx={{ fontFamily: T.fontBody, color: slepen ? T.tertiary : T.outline,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
        {bezig ? <CircularProgress size={14} /> : <Icon name="place_item" size={17} />}
        {bezig ? 'Bewaren…' : 'Sleep de klantbriefing hierheen of klik'}
      </Typography>
    </Box>
  );
}
